import { useNavigate, useSearch } from '@tanstack/react-router';
import * as React from 'react';

import type { EchangesFilters } from '@/routes/supplier/communication/echanges-model';
import { SupplierEchangesScreen } from '@/routes/supplier/communication/supplier-echanges-screen';

// =============================================================================
// supplier-echanges-route.tsx — Adaptateur routeur « Mes échanges avec JeKo » (consolidation, GATE 2026-09-11).
// =============================================================================
// Route `/echanges`. L'ÉTAT DE VUE est porté par l'URL (RULE_REF_006, pas de localStorage) :
//   • `statut` = 'archive' PRÉ-POSITIONNE la checkbox « Conversations archivées » (includeArchived). Les
//     deux futures entrées de menu (« En cours » / « Archivés ») pointeront cette page avec `statut`
//     différent — le pré-filtrage est porté par ce search param.
//   • `campaign` = filtre campagne (clé HOMOGÈNE avec /conditions, /messagerie, /archives).
//   • `from` / `to` = bornes de période (YYYY-MM-DD) sur la date de conversation.
//   • `unread` = checkbox « Conversations non lues ».
//   • `thread` = fil sélectionné (panneau détail).
// La garde d'accès reste RLS fail-closed côté serveur ; le gating UI n'est PAS la frontière de sécurité.
// Écran testable hors routeur (props d'état).

/** Forme validée du search de la route échanges (état d'URL). */
export interface SupplierEchangesSearch {
  readonly statut?: 'archive';
  readonly campaign?: string;
  readonly from?: string;
  readonly to?: string;
  readonly unread?: boolean;
  readonly thread?: string;
}

/** Valide/normalise le search (allowlist stricte, défauts non sérialisés → URL propre). */
export function validateSupplierEchangesSearch(
  search: Record<string, unknown>,
): SupplierEchangesSearch {
  const result: {
    statut?: 'archive';
    campaign?: string;
    from?: string;
    to?: string;
    unread?: boolean;
    thread?: string;
  } = {};
  if (search['statut'] === 'archive') {
    result.statut = 'archive';
  }
  const campaign = search['campaign'];
  if (typeof campaign === 'string' && campaign.length > 0) {
    result.campaign = campaign;
  }
  const from = search['from'];
  if (typeof from === 'string' && from.length > 0) {
    result.from = from;
  }
  const to = search['to'];
  if (typeof to === 'string' && to.length > 0) {
    result.to = to;
  }
  if (search['unread'] === true || search['unread'] === 'true') {
    result.unread = true;
  }
  const thread = search['thread'];
  if (typeof thread === 'string' && thread.length > 0) {
    result.thread = thread;
  }
  return result;
}

/** Projette le search d'URL en filtres de vue (booléens includeArchived/unreadOnly toujours définis). */
function toFilters(search: SupplierEchangesSearch): EchangesFilters {
  return {
    includeArchived: search.statut === 'archive',
    unreadOnly: search.unread === true,
    ...(search.campaign !== undefined ? { campaignId: search.campaign } : {}),
    ...(search.from !== undefined ? { dateFrom: search.from } : {}),
    ...(search.to !== undefined ? { dateTo: search.to } : {}),
  };
}

/** Sérialise les filtres de vue en search d'URL (défauts non sérialisés → URL propre). */
function toSearch(
  filters: EchangesFilters,
  thread: string | null,
): SupplierEchangesSearch {
  return {
    ...(filters.includeArchived ? { statut: 'archive' as const } : {}),
    ...(filters.unreadOnly ? { unread: true } : {}),
    ...(filters.campaignId !== undefined ? { campaign: filters.campaignId } : {}),
    ...(filters.dateFrom !== undefined ? { from: filters.dateFrom } : {}),
    ...(filters.dateTo !== undefined ? { to: filters.dateTo } : {}),
    ...(thread !== null ? { thread } : {}),
  };
}

export function SupplierEchangesRoute(): React.JSX.Element {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as SupplierEchangesSearch;

  const filters = toFilters(search);
  const selectedThreadId = search.thread ?? null;

  const onFiltersChange = React.useCallback(
    (next: EchangesFilters): void => {
      void navigate({
        to: '/echanges',
        search: toSearch(next, selectedThreadId),
      });
    },
    [navigate, selectedThreadId],
  );

  const onSelectThread = React.useCallback(
    (threadId: string | null): void => {
      void navigate({
        to: '/echanges',
        search: toSearch(toFilters(search), threadId),
      });
    },
    [navigate, search],
  );

  return (
    <SupplierEchangesScreen
      filters={filters}
      onFiltersChange={onFiltersChange}
      selectedThreadId={selectedThreadId}
      onSelectThread={onSelectThread}
    />
  );
}
